/**
 * Timezone utility functions for Indian Standard Time (IST)
 * IST is UTC+05:30
 */

/**
 * Convert UTC time to IST
 * @param {string|Date} utcTime - UTC time string or Date object
 * @returns {Date} Date object in IST
 */
export const convertUTCToIST = (utcTime) => {
  const date = new Date(utcTime);
  // IST is UTC+05:30 (330 minutes)
  const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
  return new Date(date.getTime() + istOffset);
};

/**
 * Convert IST time to UTC
 * @param {string|Date} istTime - IST time string or Date object
 * @returns {Date} Date object in UTC
 */
export const convertISTToUTC = (istTime) => {
  const date = new Date(istTime);
  // IST is UTC+05:30 (330 minutes)
  const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
  return new Date(date.getTime() - istOffset);
};

/**
 * Format UTC time string to IST time string (HH:MM format)
 * @param {string} utcTimeString - UTC time string (e.g., '2025-10-10T03:30:00.000Z')
 * @returns {string} IST time in HH:MM format (e.g., '09:00')
 */
export const formatUTCToISTTime = (utcTimeString) => {
  const utcDate = new Date(utcTimeString);
  const istDate = convertUTCToIST(utcDate);
  
  // Format to HH:MM
  const hours = istDate.getUTCHours().toString().padStart(2, '0');
  const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
  
  return `${hours}:${minutes}`;
};

/**
 * Format IST time string to UTC time string for API calls
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:MM format (IST)
 * @returns {string} UTC time string (e.g., '2025-10-10T03:30:00.000Z')
 */
export const formatISTTimeToUTC = (date, time) => {
  // Create IST time
  const istDateTime = new Date(`${date}T${time}:00`);
  
  // Convert to UTC
  const utcDateTime = convertISTToUTC(istDateTime);
  
  return utcDateTime.toISOString();
};

/**
 * Get current IST time
 * @returns {Date} Current date and time in IST
 */
export const getCurrentIST = () => {
  // Use the built-in JavaScript Date with IST timezone
  return new Date(new Date().toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
};

/**
 * Format date and time to IST display format
 * @param {string|Date} utcDateTime - UTC date time
 * @returns {string} Formatted IST date time
 */
export const formatToISTDisplay = (utcDateTime) => {
  const istDate = convertUTCToIST(utcDateTime);
  return istDate.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Check if a time string is in IST timezone
 * @param {string} timeString - Time string to check
 * @returns {boolean} True if the time appears to be in IST
 */
export const isISTTime = (timeString) => {
  // If time string doesn't end with 'Z' and doesn't have timezone info, 
  // assume it's local time (IST in our case)
  return !timeString.endsWith('Z') && !timeString.includes('+') && !timeString.includes('-');
};

/**
 * Create a date object for IST timezone
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:MM format (IST)
 * @returns {Date} Date object representing IST time
 */
export const createISTDateTime = (date, time) => {
  // Create date in IST (local time zone)
  return new Date(`${date}T${time}:00+05:30`);
};

/**
 * Extract time components from UTC timestamp for IST display
 * @param {string} utcTimestamp - UTC timestamp
 * @returns {Object} Object with IST time components
 */
export const extractISTTimeComponents = (utcTimestamp) => {
  const istDate = convertUTCToIST(utcTimestamp);
  
  return {
    date: istDate.toISOString().split('T')[0], // YYYY-MM-DD
    time: `${istDate.getUTCHours().toString().padStart(2, '0')}:${istDate.getUTCMinutes().toString().padStart(2, '0')}`, // HH:MM
    datetime: istDate,
    timestamp: istDate.getTime()
  };
};

// Timezone constants
export const IST_OFFSET = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
export const IST_TIMEZONE = 'Asia/Kolkata';
export const IST_OFFSET_STRING = '+05:30';